import {
    createContext,
    useContext,
    useMemo,
    ReactNode,
} from 'react';

import { useRegistryContext } from './RegistryContext';
import { useGetRegistryPatientById } from 'hooks/useRegistryPatient';

/* =========================
   Types
========================= */

type RegistryPatientContextType = {
    patient: any;
    isLoading: boolean;
    isFetching: boolean;
    refetch: () => void;
};

type RegistryPatientProviderProps = {
    patientId?: number;
    children: ReactNode;
};

/* =========================
   Context
========================= */

const RegistryPatientContext = createContext<RegistryPatientContextType | undefined>(
    undefined
);

/* =========================
   Provider
========================= */

export function RegistryPatientProvider({ patientId, children }: RegistryPatientProviderProps) {
    const { registry } = useRegistryContext();
    const {
        data: patient,
        refetch,
        isLoading,
        isFetching,
    } = useGetRegistryPatientById({ registryId: registry?.id, patientId }, {
        enabled: !!patientId && !!registry?.id,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        staleTime: 60_000,
        select: (res: any) => {
            return res.data.data;
        },
    });

    const value = useMemo(
        () => ({ registry, patient, isLoading, isFetching, refetch }),
        [registry, patient, isLoading, isFetching, refetch]
    );

    return <RegistryPatientContext.Provider value={value}>{children}</RegistryPatientContext.Provider>;
}

/* =========================
   Hook
========================= */

export function useRegistryPatientContext() {
    const context = useContext(RegistryPatientContext);

    if (!context) {
        throw new Error(
            'useRegistryPatientContext must be used within a RegistryPatientProvider'
        );
    }

    return context;
}
